import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Paperclip, Upload as UpIcon, Trash2, Download, FileText } from "lucide-react";

/**
 * EvidencePanel — list + upload evidence documents.
 * Can be filtered by kpi_id/period (KPI Detail panel) or used standalone (Evidence Manager).
 */
export default function EvidencePanel({ kpiId = null, period = null, compact = false, onChange }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    const params = {};
    if (kpiId) params.kpi_id = kpiId;
    if (period) params.period = period;
    const r = await api.get("/evidence", { params });
    setDocs(r.data);
  };
  useEffect(() => { load(); }, [kpiId, period]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (kpiId) fd.append("kpi_id", kpiId);
      if (period) fd.append("period", period);
      await api.post("/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      onChange && onChange();
    } catch (e) { setErr(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.filename}?`)) return;
    await api.delete(`/evidence/${d.id}`);
    await load();
    onChange && onChange();
  };

  const download = async (d) => {
    const token = localStorage.getItem("token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/evidence/${d.id}/download`;
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = d.filename; a.click();
  };

  return (
    <div data-testid="evidence-panel">
      <div className="flex items-center justify-between">
        <div className="section-label flex items-center gap-2"><Paperclip size={12}/> Evidence</div>
        <button
          data-testid="evidence-upload-button"
          className="btn btn-secondary"
          disabled={busy}
          onClick={()=>fileRef.current?.click()}
        >
          <UpIcon size={12}/> Upload
        </button>
        <input
          data-testid="evidence-file-input"
          ref={fileRef} type="file" className="hidden"
          accept=".pdf,.png,.jpg,.jpeg,.csv,.xlsx,.xls,.txt,.md,.docx"
          onChange={e=>{ upload(e.target.files[0]); e.target.value = ""; }}
        />
      </div>

      {err && <div className="text-status-error text-xs mt-2">{err}</div>}

      {docs.length === 0 ? (
        <div className="text-xs text-ink-soft mt-3">
          No evidence attached. Upload utility bills, certificates or audit reports to raise data confidence.
        </div>
      ) : (
        <ul className={"mt-3 divide-y divide-border " + (compact ? "max-h-56 overflow-y-auto" : "")}>
          {docs.map(d => (
            <li key={d.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`ev-${d.id}`}>
              <FileText size={14} className="text-ink-soft shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{d.filename}</div>
                <div className="text-[11px] text-ink-soft">
                  {d.kpi_id || "unlinked"} {d.period ? `· ${d.period}` : ""} · {Math.round(d.size_bytes/1024)} KB · by {d.uploaded_by}
                </div>
              </div>
              <button data-testid={`ev-dl-${d.id}`} onClick={()=>download(d)} className="btn btn-ghost"><Download size={12}/></button>
              <button data-testid={`ev-rm-${d.id}`} onClick={()=>remove(d)} className="btn btn-ghost text-status-error"><Trash2 size={12}/></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
